import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Users, Lock, Unlock, Building2, UserCircle } from 'lucide-react';
import api from '../../../shared/services/apiClient';
import toast from 'react-hot-toast';
import ConfirmDialog from '../../../shared/ui/ConfirmDialog';

// Types matching our filter system
type ScopeType = 'COMPANY' | 'BRANCH' | 'ROLE' | 'USER';

type DashboardAccess = {
  id: string;
  dashboardId: string;
  scopeType: ScopeType;
  scopeValue: string; // userId, roleId, branchId, or companyId
  canView: boolean;
  scopeLabel?: string; // Human-readable: "Juan Pérez", "Analistas", "Sucursal Centro"
};

type WidgetAccess = {
  id: string;
  widgetId: string;
  dashboardId: string;
  scopeType: ScopeType;
  scopeValue: string;
  canView: boolean;
  scopeLabel?: string;
};

type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  empresaId?: string;
};

type Dashboard = {
  id: string;
  name: string;
  companyId?: string;
};

type Props = {
  dashboardId: string;
  onClose: () => void;
};

const SCOPE_TYPES = [
  { value: 'USER' as ScopeType, label: 'Usuario específico', icon: UserCircle },
  { value: 'ROLE' as ScopeType, label: 'Por rol', icon: Users },
  { value: 'BRANCH' as ScopeType, label: 'Por sucursal', icon: Building2 },
  { value: 'COMPANY' as ScopeType, label: 'Toda la empresa', icon: Building2 },
];

const ROLES = [
  { value: 'ADMIN', label: 'Administradores' },
  { value: 'ANALYST', label: 'Analistas' },
  { value: 'CLIENT', label: 'Clientes' },
];

const AccessManagementPanel: React.FC<Props> = ({ dashboardId, onClose }) => {
  const [dashboardAccess, setDashboardAccess] = useState<DashboardAccess[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNewAccess, setShowNewAccess] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; accessId: string | null }>({ 
    isOpen: false, 
    accessId: null 
  });

  // New access form
  const [newAccess, setNewAccess] = useState({
    scopeType: 'USER' as ScopeType,
    scopeValue: '',
    canView: true,
  });

  useEffect(() => {
    loadPanelData();
  }, [dashboardId]);

  const buildScopeLabel = (access: DashboardAccess, availableUsers: User[]) => {
    if (access.scopeType === 'USER') {
      const user = availableUsers.find((item) => item.id === access.scopeValue);
      return user?.name || user?.email || 'Usuario desconocido';
    }
    if (access.scopeType === 'ROLE') {
      const role = ROLES.find((item) => item.value === access.scopeValue);
      return role ? role.label : access.scopeValue;
    }
    if (access.scopeType === 'BRANCH') {
      return `Sucursal ${access.scopeValue}`;
    }
    if (access.scopeType === 'COMPANY') {
      return 'Toda la empresa';
    }
    return access.scopeValue;
  };

  const loadPanelData = async () => {
    setLoading(true);
    try {
      const [dashResponse, usersResponse, accessResponse] = await Promise.all([
        api.get(`/dashboards/${dashboardId}`),
        api.get('/users'),
        api.get(`/dashboards/${dashboardId}/access`)
      ]);
      const dashData = dashResponse.data;
      const mapped = { ...dashData, companyId: dashData.companyId || dashData.empresaId };
      setDashboard(mapped as any);

      const allUsers = usersResponse.data as User[];

      if (!mapped.companyId) {
        console.warn('⚠️ Dashboard sin companyId:', mapped.id, mapped.name);
        setUsers([]);
        setDashboardAccess([]);
        toast.error('Este dashboard no tiene empresa asignada. No se pueden configurar permisos.');
        return;
      }

      const eligibleUsers = allUsers.filter((u: User) => {
        const dashCompanyStr = String(mapped.companyId);
        const userCompanyStr = u.empresaId ? String(u.empresaId) : null;
        return userCompanyStr === dashCompanyStr;
      });

      setUsers(eligibleUsers);

      if (eligibleUsers.length === 0) {
        toast.error('No hay usuarios de esta empresa para asignar permisos');
      }

      const access = (accessResponse.data as DashboardAccess[]).map((item) => ({
        ...item,
        scopeLabel: buildScopeLabel(item, eligibleUsers),
      }));
      setDashboardAccess(access);
    } catch (error) {
      console.error('Error loading dashboard/users/access:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccess = async () => {
    if (!newAccess.scopeValue && newAccess.scopeType !== 'COMPANY') {
      toast.error('Por favor selecciona un valor');
      return;
    }

    try {
      // VALIDACIÓN: Usuario debe ser de la misma empresa
      if (newAccess.scopeType === 'USER') {
        const selectedUser = users.find(u => u.id === newAccess.scopeValue);
        if (!selectedUser) {
          toast.error('Usuario no encontrado o no pertenece a esta empresa');
          return;
        }
        if (dashboard?.companyId && selectedUser.empresaId !== dashboard.companyId) {
          toast.error('🔒 No puedes dar acceso a usuarios de otra empresa');
          return;
        }
      }

      const payload = {
        scopeType: newAccess.scopeType,
        scopeValue: newAccess.scopeType === 'COMPANY' ? undefined : newAccess.scopeValue,
        canView: newAccess.canView,
      };
      const response = await api.post(`/dashboards/${dashboardId}/access`, payload);
      const created = response.data as DashboardAccess;
      const access: DashboardAccess = {
        ...created,
        scopeLabel: buildScopeLabel(created, users),
      };

      setDashboardAccess((prev) => [...prev, access]);

      toast.success('Permiso agregado');
      setShowNewAccess(false);
      setNewAccess({
        scopeType: 'USER',
        scopeValue: '',
        canView: true,
      });
    } catch (error) {
      console.error('Error adding access:', error);
      toast.error('Error al agregar permiso');
    }
  };

  const handleDeleteAccess = async (accessId: string) => {
    try {
      await api.delete(`/dashboards/${dashboardId}/access/${accessId}`);
      const updated = dashboardAccess.filter(a => a.id !== accessId);
      setDashboardAccess(updated);

      toast.success('Permiso eliminado');
      setConfirmDelete({ isOpen: false, accessId: null });
    } catch (error) {
      console.error('Error deleting access:', error);
      toast.error('Error al eliminar permiso');
    }
  };

  const getScopeIcon = (scopeType: ScopeType) => {
    const scope = SCOPE_TYPES.find(s => s.value === scopeType);
    return scope ? scope.icon : Users;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Lock className="w-6 h-6 text-sky-400" />
            <div>
              <h2 className="text-xl font-bold text-white">Control de Acceso</h2>
              <p className="text-sm text-slate-400 mt-1">
                Define quién puede ver este dashboard
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-400"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info box */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-200">
                  <strong>¿Cómo funciona?</strong> Los permisos se evalúan de más específico a más general:
                  Usuario → Rol → Sucursal → Empresa. Si un usuario tiene acceso directo, ese permiso tiene prioridad.
                </p>
              </div>

              {/* Dashboard without company - ERROR */}
              {dashboard && !dashboard.companyId && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-sm text-red-200">
                    <strong>⚠️ Error de configuración:</strong> Este dashboard no tiene empresa asignada (companyId: null).
                    No se pueden configurar permisos hasta que se asigne a una empresa.
                  </p>
                </div>
              )}

              {/* Company boundary warning */}
              {dashboard && dashboard.companyId && (
                <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                  <p className="text-sm text-sky-200">
                    <strong>🔒 Jerarquía estricta:</strong> Solo puedes dar permisos a usuarios de la misma empresa que este dashboard.
                    {users.length === 0 && ' No hay usuarios elegibles.'}
                  </p>
                </div>
              )}

              {/* Access list */}
              {dashboardAccess.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Unlock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Sin restricciones configuradas</p>
                  <p className="text-sm mt-1">
                    El dashboard es visible para todos los usuarios
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dashboardAccess.map((access) => {
                    const Icon = getScopeIcon(access.scopeType);
                    return (
                      <div
                        key={access.id}
                        className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between hover:bg-slate-700 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 text-sky-400" />
                          <div>
                            <p className="text-white font-medium">
                              {access.scopeLabel}
                            </p>
                            <p className="text-sm text-slate-400">
                              {access.canView ? 'Puede ver el dashboard' : 'Sin acceso'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setConfirmDelete({ isOpen: true, accessId: access.id })}
                          className="text-slate-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add button */}
              <button
                onClick={() => setShowNewAccess(true)}
                disabled={!dashboard?.companyId}
                className={`w-full px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium ${
                  dashboard?.companyId
                    ? 'bg-sky-500 hover:bg-sky-600 text-white'
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }`}
                title={!dashboard?.companyId ? 'Dashboard sin empresa asignada' : 'Agregar nuevo permiso'}
              >
                <Plus className="w-5 h-5" />
                Agregar Permiso
              </button>
            </div>
          )}
        </div>

        {/* New Access Modal */}
        {showNewAccess && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 rounded-xl">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-lg w-full shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4">Nuevo Permiso</h3>
              
              <div className="space-y-4">
                {/* Scope Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Tipo de permiso
                  </label>
                  <select
                    value={newAccess.scopeType}
                    onChange={(e) => setNewAccess(prev => ({ 
                      ...prev, 
                      scopeType: e.target.value as ScopeType,
                      scopeValue: '' // Reset value when type changes
                    }))}
                    className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    {SCOPE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Scope Value - conditional based on type */}
                {newAccess.scopeType === 'USER' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Seleccionar usuario
                    </label>
                    {users.length === 0 ? (
                      <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                        <p className="text-sm text-slate-400">
                          No hay usuarios de esta empresa disponibles
                        </p>
                      </div>
                    ) : (
                      <select
                        value={newAccess.scopeValue}
                        onChange={(e) => setNewAccess(prev => ({ ...prev, scopeValue: e.target.value }))}
                        className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                      >
                        <option value="">Selecciona un usuario</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {newAccess.scopeType === 'ROLE' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Seleccionar rol
                    </label>
                    <select
                      value={newAccess.scopeValue}
                      onChange={(e) => setNewAccess(prev => ({ ...prev, scopeValue: e.target.value }))}
                      className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="">Selecciona un rol</option>
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {newAccess.scopeType === 'BRANCH' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Sucursal
                    </label>
                    <input
                      type="text"
                      value={newAccess.scopeValue}
                      onChange={(e) => setNewAccess(prev => ({ ...prev, scopeValue: e.target.value }))}
                      placeholder="ID de la sucursal"
                      className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Nota: En el futuro, esto será un selector de sucursales
                    </p>
                  </div>
                )}

                {newAccess.scopeType === 'COMPANY' && (
                  <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                    <p className="text-sm text-sky-200">
                      Este permiso dará acceso a <strong>todos los usuarios</strong> de la empresa.
                    </p>
                  </div>
                )}

                {/* Access toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="canView"
                    checked={newAccess.canView}
                    onChange={(e) => setNewAccess(prev => ({ ...prev, canView: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-600 text-sky-500 focus:ring-sky-500"
                  />
                  <label htmlFor="canView" className="text-sm text-slate-300">
                    Permitir acceso (si está desmarcado, el usuario NO podrá ver el dashboard)
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowNewAccess(false)}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddAccess}
                  className="flex-1 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors font-medium"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Delete Dialog */}
        <ConfirmDialog
          isOpen={confirmDelete.isOpen}
          title="Eliminar permiso"
          message="¿Estás seguro de que deseas eliminar este permiso de acceso?"
          onConfirm={() => {
            if (confirmDelete.accessId) {
              handleDeleteAccess(confirmDelete.accessId);
            }
          }}
          onCancel={() => setConfirmDelete({ isOpen: false, accessId: null })}
        />
      </div>
    </div>
  );
};

export default AccessManagementPanel;
